/* eslint-disable max-lines-per-function */
/* eslint-disable react/react-in-jsx-scope */
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Card, CardHeader, Collapse, Grid, IconButton } from '@mui/material';
import ErrorFallback from '~/components/ErrorFallback';
import Layout from '~/components/Layout';
import { useLang } from '~/hooks/useLang';
import notify from '~/utils/notify';
import { useHookstate } from '@hookstate/core';
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies';
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups';
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch';
import CancelIcon from '@mui/icons-material/Cancel';
import useReportState from '~/features/Report/stores/ReportState';
import { downloadReportCSV } from '~/features/Report/services/report';
import ExportButtonCSV from '~/features/Report/components/ButtonExportCSV';
import GenerateReportButton from '~/features/Report/components/GenerateReportButton';
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet';
import { makeRequestTogetReportDispatchesStatisticsByommander } from './services';
import { generateCSVFileName } from '~/utils/strings';

export default function DispatchesStatisticsByommander() {
  const expandFilterSearch = useHookstate<boolean>(true);
  const { translate } = useLang();
  const isLoading = useHookstate<boolean>(false);
  const lockFields = useHookstate<boolean>(false);
  const startDate = useHookstate<string | null>(null);
  const finishDate = useHookstate<string | null>(null);
  const period = useHookstate('0')
  const cpf = useHookstate<string | null>(null)
  const plate = useHookstate<string | null>(null)
  const userId = useHookstate<number | null>(null)
  const groupSetId = useHookstate<number | null>(null)
  const agencyIds = useHookstate<readonly number[]>([]);
  const stationDispatchGroups = useHookstate<readonly number[]>([]);
  const labelButton = useHookstate('');
  const groupId = useHookstate<readonly number[]>([]);
  const resetValeus = useHookstate(false)

  const { reportData, loadingReport } = useReportState();

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  async function generateReport(exportCSV = false) {
    if (startDate && finishDate) {
      loadingReport().set(true)
      isLoading.set(true)
      const data = await makeRequestTogetReportDispatchesStatisticsByommander({
        startDate: startDate.get(),
        finishDate: finishDate.get(),
        agencyIds: agencyIds.get(),
        dispatchGroupsIds: groupId.get(),
        groupSetId: groupSetId.get(),
        userId: userId.get(),
        exportCSV: exportCSV,
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
      })
      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }

      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('Occurrences statistics by commander', translate),
          data
        )
      } else {
        setTimeout(() => {
          reportData().set(data)
        })
        window.open(`/report/result/occurrences-statistics-by-commander`)
      }
    } else {
      notify({ message: translate('Initial and final date are mandatory'), type: 'error' })
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true} >
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader title={`${translate('Report')}: ${translate(`Occurrences statistics by commander`)}`} />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader title={`${translate('Filters')}`}
            avatar={(
              <IconButton
                onClick={() => expandFilterSearch.set(!expandFilterSearch.get())}
                sx={{ display: 'flex', justifyContent: 'end', marginRight: 1, padding: 0 }}>
                {expandFilterSearch.value ? (<ExpandLessIcon />) : (<ExpandMoreIcon />)}
              </IconButton>
            )}
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }} />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchsGroups
                    disabled={lockFields.get()}
                    agencyId={agencyIds.get() ?? undefined}
                    dispatcheGroups={groupId.get() ?? undefined}
                    onGroupDispatchChange={(group) => {
                      groupId.set(group)
                      // deviceId.set(null)
                      labelButton.set(translate('Search'))
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchGroupSet
                    disabled={lockFields.get()}
                    dispatchGroupSetId={groupSetId.get() ?? undefined}
                    onGroupDispatchSetChange={(group) => {
                      groupSetId.set(group)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteFetch
                    label={'User'}
                    urlFetch={'/user/get'}
                    size={"small"}
                    disabled={lockFields.get()}
                    onFetchChange={(user: number | null) => userId.set(user)}
                    resetValues={resetValeus.get()}
                    valueId={userId.get() ? Number(userId.get()) : undefined}
                  />
                </Grid>
                <Grid container direction={'row'}>
                  <FilterSelectPeriod
                    disabled={lockFields.get()}
                    onStartDateChange={(value) => startDate.set(value?.toJSON() ?? null)}
                    onFinishDateChange={(value) => finishDate.set(value?.toJSON() ?? null)}
                    onPeriodChange={(value) => { period.set(value) }}
                    period={period.get()}
                  />
                </Grid>
              </Grid>

              <Grid item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  pt: 2
                }}>
                <Button variant="outlined"
                  onClick={() => {
                    startDate.set(null)
                    finishDate.set(null)
                    agencyIds.set([])
                    groupId.set([])
                    groupSetId.set(null)
                    userId.set(null)
                    period.set('0')
                  }}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={() => generateReport()}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary >
  )
}
